import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Sparkles, Target, Globe, Shield } from 'lucide-react';
import { useProductTour } from '../../../contexts/ProductTourContext';
import { TourSection, TOUR_SAMPLE_DATA } from '../../../constants/tour';
import { isMobileDevice } from '../../tour/tourUtils';

interface TourReplayCardProps {
  onClose: () => void;
}

const SECTION_CONFIG: {
  section: TourSection;
  label: string;
  desc: string;
  icon: React.ElementType;
}[] = [
  { section: 'discovery', label: 'Discovery', desc: 'Search & signals', icon: Sparkles },
  { section: 'dataset', label: 'Leads', desc: 'Working with data', icon: Target },
  { section: 'navigation', label: 'Navigation', desc: 'App layout', icon: Globe },
  { section: 'settings', label: 'Settings', desc: 'Configuration', icon: Shield },
];

export const TourReplayCard: React.FC<TourReplayCardProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const { resetTour, startTour, startTourAtSection } = useProductTour();

  const handleFullTour = () => {
    resetTour();
    onClose();
    navigate('/datasets');
    setTimeout(() => startTour(), 500);
  };

  const handleSectionTour = (section: TourSection) => {
    onClose();

    if (section === 'dataset') {
      // Start tour BEFORE navigation so useTableData sees isTourActive=true and loads mock data
      startTourAtSection(section);
      // Close chat panel on mobile before navigation
      if (isMobileDevice()) {
        window.dispatchEvent(new CustomEvent('tour-close-chat-panel'));
      }
      // Navigate to mock dataset - ProductTour will wait for elements to be ready
      navigate(`/datasets/${TOUR_SAMPLE_DATA.datasetId}`);
      return;
    }

    navigate('/datasets');
    setTimeout(() => startTourAtSection(section), 500);
  };

  return (
    <div data-tour="replay-tour-section">
      {/* Full Tour Button */}
      <button
        onClick={handleFullTour}
        className="group flex items-center gap-3 px-4 py-3 text-[13px] font-medium rounded-xl w-full mb-3 bg-gradient-to-br from-neutral-800 to-neutral-600 text-white border-none transition-all duration-200 hover:shadow-md"
      >
        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/15 transition-all duration-200 group-hover:scale-105">
          <Play className="w-3.5 h-3.5 text-white ml-0.5" fill="white" />
        </div>
        <div className="flex flex-col items-start">
          <span className="font-semibold">Full Tour</span>
          <span className="text-[11px] text-neutral-300 font-normal">Complete walkthrough</span>
        </div>
      </button>

      {/* Section-specific buttons */}
      <div className="grid grid-cols-2 gap-2">
        {SECTION_CONFIG.map(({ section, label, desc, icon: Icon }) => (
          <button
            key={section}
            onClick={() => handleSectionTour(section)}
            className="group flex items-center gap-2.5 px-3 py-2.5 text-[12px] font-medium rounded-lg bg-slate-50 text-neutral-700 border border-neutral-200 transition-all duration-200 hover:shadow-sm hover:border-neutral-300"
          >
            <div className="flex items-center justify-center w-7 h-7 rounded-md bg-neutral-100 transition-all duration-200">
              <Icon className="w-3.5 h-3.5 text-neutral-500" />
            </div>
            <div className="flex flex-col items-start">
              <span className="font-medium text-neutral-700">{label}</span>
              <span className="text-[10px] text-neutral-400 font-normal">{desc}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};
